import { useState, useEffect } from 'react';
import {
  PlusIcon,
  TrophyIcon,
  CalendarIcon,
  MapPinIcon,
  PencilIcon,
  TrashIcon,
  TagIcon,
} from '@heroicons/react/24/outline';
import { getTournaments } from '../../../lib/supabase';
import { supabase } from '../../../lib/supabase';
import MobileTournamentSheet from '../../components/MobileTournamentSheet';
import MobileTournamentCategoriesRounds from '../../components/MobileTournamentCategoriesRounds';
import DeleteConfirmationModal from '../../../components/DeleteConfirmationModal';
import { toast } from 'react-hot-toast';

interface Tournament {
  id: string;
  name: string;
  start_date: string;
  end_date: string;
  venue?: string;
  status: string;
  description?: string;
  prize_pool?: number;
  registration_fee?: number;
}

function statusColor(status: string) {
  switch (status?.toLowerCase()) {
    case 'upcoming': return 'bg-blue-100 text-blue-700';
    case 'active': return 'bg-green-100 text-green-700';
    case 'completed': return 'bg-gray-100 text-gray-600';
    default: return 'bg-amber-100 text-amber-700';
  }
}

export default function MobileAdminTournaments() {
  const [tournaments, setTournaments] = useState<Tournament[]>([]);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingTournament, setEditingTournament] = useState<Tournament | null>(null);
  const [deletingTournament, setDeletingTournament] = useState<Tournament | null>(null);
  const [categoriesRoundsTournament, setCategoriesRoundsTournament] = useState<Tournament | null>(null);

  useEffect(() => { fetchTournaments(); }, []);

  const fetchTournaments = async () => {
    try {
      setLoading(true);
      const { data, error } = await getTournaments();
      if (error) throw error;
      setTournaments(data || []);
    } catch {
      toast.error('Failed to load tournaments');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!deletingTournament) return;
    try {
      const { error } = await supabase.from('tournaments').delete().eq('id', deletingTournament.id);
      if (error) throw error;
      toast.success('Tournament deleted');
      setDeletingTournament(null);
      fetchTournaments();
    } catch {
      toast.error('Failed to delete tournament');
    }
  };

  return (
    <div className="px-4 py-4 space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-xs text-gray-500">{tournaments.length} tournament{tournaments.length !== 1 ? 's' : ''}</p>
        <button
          onClick={() => { setEditingTournament(null); setIsModalOpen(true); }}
          className="bg-golf-orange text-white rounded-xl px-3 py-2.5 flex items-center gap-1.5 text-sm font-medium"
        >
          <PlusIcon className="w-4 h-4" />
          New
        </button>
      </div>

      {loading ? (
        <div className="flex justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-4 border-golf-orange border-t-transparent" />
        </div>
      ) : tournaments.length === 0 ? (
        <div className="text-center py-16">
          <TrophyIcon className="w-12 h-12 text-gray-200 mx-auto mb-3" />
          <p className="text-gray-400 text-sm">No tournaments yet</p>
        </div>
      ) : (
        <div className="space-y-3">
          {tournaments.map(t => (
            <div key={t.id} className="bg-white rounded-2xl border border-gray-100 shadow-sm p-4">
              <div className="flex items-start justify-between gap-2 mb-3">
                <div className="flex-1 min-w-0">
                  <p className="font-bold text-golf-gray text-sm leading-snug">{t.name}</p>
                  <span className={`inline-flex mt-1 text-xs px-2 py-0.5 rounded-full font-medium capitalize ${statusColor(t.status)}`}>
                    {t.status}
                  </span>
                </div>
                <div className="flex items-center gap-1">
                  <button
                    onClick={() => { setEditingTournament(t); setIsModalOpen(true); }}
                    className="p-2 rounded-xl text-gray-400 hover:text-golf-orange hover:bg-golf-orange/5 transition-colors"
                  >
                    <PencilIcon className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => setDeletingTournament(t)}
                    className="p-2 rounded-xl text-gray-400 hover:text-red-500 hover:bg-red-50 transition-colors"
                  >
                    <TrashIcon className="w-4 h-4" />
                  </button>
                </div>
              </div>
              <div className="space-y-1.5">
                <div className="flex items-center gap-1.5 text-xs text-gray-500">
                  <CalendarIcon className="w-3.5 h-3.5" />
                  <span>
                    {new Date(t.start_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                    {' — '}
                    {new Date(t.end_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                  </span>
                </div>
                {t.venue && (
                  <div className="flex items-center gap-1.5 text-xs text-gray-500">
                    <MapPinIcon className="w-3.5 h-3.5" />
                    <span className="truncate">{t.venue}</span>
                  </div>
                )}
                {t.prize_pool && (
                  <div className="flex items-center gap-1.5 text-xs text-gray-500">
                    <TrophyIcon className="w-3.5 h-3.5" />
                    <span>₦{(t.prize_pool / 1_000_000).toFixed(1)}M Prize Pool</span>
                  </div>
                )}
              </div>
              <button
                onClick={() => setCategoriesRoundsTournament(t)}
                className="mt-3 w-full flex items-center justify-center gap-1.5 py-2 rounded-xl bg-gray-50 border border-gray-100 text-xs font-medium text-gray-600 hover:bg-golf-orange/5 hover:text-golf-orange hover:border-golf-orange/20 transition-colors"
              >
                <TagIcon className="w-3.5 h-3.5" />
                Categories & Rounds
              </button>
            </div>
          ))}
        </div>
      )}

      <MobileTournamentSheet
        isOpen={isModalOpen}
        onClose={() => { setIsModalOpen(false); setEditingTournament(null); fetchTournaments(); }}
        tournament={editingTournament}
      />

      <DeleteConfirmationModal
        isOpen={!!deletingTournament}
        onClose={() => setDeletingTournament(null)}
        onConfirm={handleDelete}
        title="Delete Tournament"
        message={`Are you sure you want to delete "${deletingTournament?.name}"? This cannot be undone.`}
      />

      {categoriesRoundsTournament && (
        <MobileTournamentCategoriesRounds
          isOpen={!!categoriesRoundsTournament}
          onClose={() => setCategoriesRoundsTournament(null)}
          tournamentId={categoriesRoundsTournament.id}
          tournamentName={categoriesRoundsTournament.name}
        />
      )}
    </div>
  );
}
